import { Link } from 'react-router-dom';

export default function HomePage() {
  return (
    <div className="space-y-8">
      {/* Hero Section */}
      <div className="text-center py-12">
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
          Turn ideas into ready-to-publish videos
        </h1>
        <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
          Vidmastro generates bilingual biblical tales, creates scenes & thumbnails with AI, 
          lets you record or upload audio, and schedules your YouTube publish — all in one place.
        </p>
      </div>

      {/* Features Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="text-3xl mb-4">📚</div>
          <h3 className="text-lg font-semibold mb-2">Bilingual tale generator</h3>
          <p className="text-gray-600 dark:text-gray-300">
            English + French biblical narratives with hook, setup, lesson, and outro sections.
          </p>
        </div>

        <div className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="text-3xl mb-4">🎨</div>
          <h3 className="text-lg font-semibold mb-2">AI images & thumbnails</h3>
          <p className="text-gray-600 dark:text-gray-300">
            12 AI-generated scenes plus 4 custom thumbnails automatically created and stored.
          </p>
        </div>

        <div className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="text-3xl mb-4">🎙️</div>
          <h3 className="text-lg font-semibold mb-2">Voice & music integration</h3>
          <p className="text-gray-600 dark:text-gray-300">
            Record narration in-app or upload your own voice tracks and background music.
          </p>
        </div>

        <div className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="text-3xl mb-4">📦</div>
          <h3 className="text-lg font-semibold mb-2">Storyboard packaging</h3>
          <p className="text-gray-600 dark:text-gray-300">
            Bundle all assets and metadata together; preview everything before publishing.
          </p>
        </div>

        <div className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="text-3xl mb-4">⏰</div>
          <h3 className="text-lg font-semibold mb-2">YouTube scheduling</h3>
          <p className="text-gray-600 dark:text-gray-300">
            Set local publish times; automated cron jobs promote videos to public when ready.
          </p>
        </div>

        <div className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="text-3xl mb-4">📊</div>
          <h3 className="text-lg font-semibold mb-2">Dashboard & alerts</h3>
          <p className="text-gray-600 dark:text-gray-300">
            Track your entire pipeline, monitor failures, and manage quotas in real-time.
          </p>
        </div>
      </div>

      {/* How it works */}
      <div className="py-12">
        <h2 className="text-3xl font-bold text-center mb-8">How it works</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">📖</span>
            </div>
            <h3 className="font-semibold mb-2">1. Pick a verse</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Choose random or specific biblical verse and generate a compelling bilingual tale.
            </p>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">🎨</span>
            </div>
            <h3 className="font-semibold mb-2">2. Auto-illustrate</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              AI creates scene prompts and generates beautiful images plus eye-catching thumbnails.
            </p>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">🎵</span>
            </div>
            <h3 className="font-semibold mb-2">3. Add audio</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Record narration directly in-app or upload your own voice track and background music.
            </p>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 bg-orange-100 dark:bg-orange-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">🚀</span>
            </div>
            <h3 className="font-semibold mb-2">4. Schedule & publish</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Set your preferred publish time and let our system handle the YouTube upload automatically.
            </p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg p-8 text-center">
        <h2 className="text-2xl font-bold mb-4">Ready to get started?</h2>
        <p className="text-gray-600 dark:text-gray-300 mb-6">
          Create your first tale and start building amazing content today.
        </p>
        <div className="flex gap-4 justify-center">
          <Link 
            to="/tales" 
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
          >
            Create New Tale
          </Link>
          <Link 
            to="/dashboard" 
            className="px-6 py-3 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-lg font-medium transition-colors"
          >
            View Dashboard
          </Link>
        </div>
      </div>
    </div>
  );
}
